import { useEffect, useState } from "react";
import { api, eur, dateFr } from "@/lib/rentApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, X, Clock } from "lucide-react";

const STATUS = {
  en_attente: { label: "En attente", variant: "secondary", icon: Clock },
  acceptée: { label: "Acceptée", variant: "default", icon: Check },
  refusée: { label: "Refusée", variant: "destructive", icon: X },
};

export default function Applications() {
  const { user } = useAuth();
  const isOwner = user.role === "proprietaire" || user.role === "admin";
  const [items, setItems] = useState(null);

  const load = () => api.get("/applications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const decide = async (id, status) => {
    await api.put(`/applications/${id}`, { status });
    toast.success(`Candidature ${status}`);
    load();
  };

  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader
        title={isOwner ? "Candidatures reçues" : "Mes candidatures"}
        subtitle={isOwner ? "Examinez les dossiers des candidats." : "Suivez l'état de vos demandes de location."}
      />
      {items.length === 0 ? (
        <EmptyState title="Aucune candidature" desc={isOwner ? "Les candidatures apparaîtront ici." : "Postulez depuis les annonces disponibles."} />
      ) : (
        <div className="space-y-4">
          {items.map((a) => {
            const st = STATUS[a.status] || STATUS.en_attente;
            return (
              <div key={a.id} className="p-5 rounded-xl border border-border bg-card flex flex-wrap items-center justify-between gap-4" data-testid={`application-${a.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold">{a.property_title}</h3>
                    <Badge variant={st.variant}>{st.label}</Badge>
                  </div>
                  {isOwner ? (
                    <p className="text-sm text-muted-foreground mt-1">
                      {a.candidate_name} · {a.profession || "—"} · Revenu {eur(a.income)} · {a.candidate_email}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1">Envoyée le {dateFr(a.created_at)}</p>
                  )}
                  {a.message && <p className="text-sm mt-2 text-foreground/80 italic">« {a.message} »</p>}
                </div>
                {isOwner && a.status === "en_attente" && (
                  <div className="flex gap-2">
                    <Button size="sm" className="rounded-full gap-1" onClick={() => decide(a.id, "acceptée")} data-testid={`accept-${a.id}`}><Check className="w-4 h-4" /> Accepter</Button>
                    <Button size="sm" variant="outline" className="rounded-full gap-1 text-destructive" onClick={() => decide(a.id, "refusée")} data-testid={`reject-${a.id}`}><X className="w-4 h-4" /> Refuser</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
